<template>
  <v-container fluid>
    <v-row>
      <!-- LOG FILES card: 30% width -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-history</v-icon>
            Audit Trail
            <v-spacer />
            <v-row class="d-flex flex-wrap" no-gutters>
              <v-col cols="12" sm="auto" class="pa-0 mb-2 mb-sm-0 mr-sm-2">
                <v-btn
                  block
                  small
                  color="primary"
                  class="white--text font-weight-bold"
                  @click="fetchLogFiles"
                  title="Refresh log files"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  REFRESH
                </v-btn>
              </v-col>
              <v-col cols="12" sm="auto" class="pa-0 mb-2 mb-sm-0 mr-sm-2">
                <v-btn
                  block
                  small
                  color="red"
                  class="white--text font-weight-bold"
                  :disabled="!hasEmptyFiles"
                  @click="confirmBulkDelete"
                  title="Delete all empty log files"
                >
                  <v-icon left small>mdi-delete-sweep</v-icon>
                  DELETE ALL EMPTY
                </v-btn>
              </v-col>
              <v-col cols="12" sm="auto" class="pa-0">
                <v-btn
                  block
                  small
                  color="grey"
                  class="white--text font-weight-bold"
                  @click="clearAllFilters"
                  title="Clear all filters"
                >
                  <v-icon left small>mdi-filter-remove</v-icon>
                  CLEAR FILTERS
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider />
          <!-- Filtering Controls -->
          <v-row class="px-4 pt-2" dense>
            <v-col cols="12" class="pb-1">
              <v-text-field
                v-model="logFileSearch"
                append-icon="mdi-magnify"
                label="Search log files"
                dense
                hide-details
                solo
              />
            </v-col>
            <v-col cols="6" class="pb-1">
              <v-menu
                ref="fromMenu"
                v-model="fromMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateFrom"
                    label="From"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filterDateFrom"
                  @input="fromMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="6" class="pb-1">
              <v-menu
                ref="toMenu"
                v-model="toMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateTo"
                    label="To"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="filterDateTo" @input="toMenu = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" class="pb-1">
              <v-select
                v-model="filterType"
                :items="logFileTypes"
                label="Type"
                dense
                hide-details
                solo
              />
            </v-col>
            <v-col cols="12" class="pb-1">
              <v-select
                v-model="filterSize"
                :items="logFileSizes"
                label="Size"
                dense
                hide-details
                solo
              />
            </v-col>
          </v-row>
          <div class="log-files-list">
            <v-list dense>
              <v-list-item
                v-for="file in filteredLogFiles"
                :key="file.name"
                :value="file.name"
                @click="selectLogFile(file)"
                :class="{
                  'grey lighten-3':
                    selectedFile && selectedFile.name === file.name,
                }"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ file.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ file.size | formatSize }} •
                    {{ file.modified | formatDate }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click.stop="downloadLogFile(file)">
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    color="red"
                    @click.stop="confirmDeleteLogFile(file)"
                    :title="'Delete file'"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-card>
        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="400">
          <v-card>
            <v-card-title class="headline">Delete Log File</v-card-title>
            <v-card-text>
              Are you sure you want to delete
              <strong>{{ fileToDelete?.name }}</strong
              >?
              <!-- Remove the warning about only empty files -->
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="deleteDialog = false">Cancel</v-btn>
              <v-btn color="red" text @click="deleteLogFile">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- Bulk Delete Confirmation Dialog -->
        <v-dialog v-model="bulkDeleteDialog" max-width="400">
          <v-card>
            <v-card-title class="headline"
              >Delete All Empty Log Files</v-card-title
            >
            <v-card-text>
              Are you sure you want to delete
              <strong>{{ emptyFilesCount }}</strong> empty log file<span
                v-if="emptyFilesCount > 1"
                >s</span
              >?
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="bulkDeleteDialog = false">Cancel</v-btn>
              <v-btn color="red" text @click="bulkDeleteEmptyFiles"
                >Delete All</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>

      <!-- RIGHT SIDE: SUMMARY (top) + LOG ENTRIES (bottom), 70% width -->
      <v-col cols="12" md="8" lg="9">
        <v-row>
          <!-- SUMMARY card: 100% of right column, top -->
          <v-col cols="12">
            <v-card flat class="pa-2 summary-bar">
              <v-row align="center" justify="space-between" no-gutters>
                <v-col cols="3" class="text-center">
                  <v-icon color="error" left>mdi-alert-circle</v-icon>
                  <span class="summary-label">Errors</span>
                  <span class="summary-value error--text">{{
                    summaryStats.error
                  }}</span>
                </v-col>
                <v-col cols="3" class="text-center">
                  <v-icon color="warning" left>mdi-alert</v-icon>
                  <span class="summary-label">Warnings</span>
                  <span class="summary-value warning--text">{{
                    summaryStats.warn
                  }}</span>
                </v-col>
                <v-col cols="3" class="text-center">
                  <v-icon color="info" left>mdi-information</v-icon>
                  <span class="summary-label">Info</span>
                  <span class="summary-value info--text">{{
                    summaryStats.info
                  }}</span>
                </v-col>
                <v-col cols="3" class="text-center">
                  <v-icon color="success" left>mdi-check-circle</v-icon>
                  <span class="summary-label">Success</span>
                  <span class="summary-value success--text">{{
                    summaryStats.success
                  }}</span>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <!-- LOG ENTRIES card: 100% of right column, below summary -->
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon left>mdi-magnify</v-icon>
                Log Entries
                <v-spacer />
                <v-text-field
                  v-model="filters.keyword"
                  append-icon="mdi-magnify"
                  label="Search"
                  dense
                  hide-details
                  solo
                  style="max-width: 200px"
                  @keyup.enter="fetchLogEntries"
                />
                <v-select
                  v-model="filters.level"
                  :items="logLevels"
                  label="Level"
                  dense
                  hide-details
                  solo
                  style="max-width: 120px"
                  @change="fetchLogEntries"
                />
                <v-btn icon @click="fetchLogEntries">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers"
                :items="paginatedLogEntries"
                :loading="loading"
                :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                class="elevation-1"
                dense
                :page.sync="pagination.page"
                :items-per-page.sync="pagination.itemsPerPage"
                :server-items-length="pagination.total"
                @click:row="showLogDetailGroup"
              >
                <template v-slot:[`item.timestamp`]="{ item }">
                  {{ item.timestamp | formatDateTime }}
                </template>
                <template v-slot:[`item.level`]="{ item }">
                  <v-chip
                    :color="levelColor(item.status || item.level)"
                    dark
                    small
                  >
                    {{
                      item.status ||
                      (item.level ? item.level.toUpperCase() : "")
                    }}
                  </v-chip>
                </template>
                <template v-slot:[`item.userDisplay`]="{ item }">
                  <span>{{ item.userDisplay }}</span>
                </template>
                <template v-slot:[`item.eventTitle`]="{ item }">
                  <span>
                    {{
                      formatEventTitle(
                        item.eventTitle || item.action || "Event"
                      )
                    }}
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-alert type="info" border="left" colored-border>
                    No log entries found for the selected file and filters.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        <!-- Grouped Log Detail Dialog -->
        <v-dialog v-model="logDetailDialog" max-width="900">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-file-document-outline</v-icon>
              Event Details
              <v-spacer />
              <v-btn icon @click="logDetailDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div v-if="groupedLogDetails.length">
                <!-- Only show the grouped meta/info in the left column ONCE, but show all raw lines in the right column as a group -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="caption"
                            >User</v-list-item-title
                          >
                          <v-list-item-subtitle>
                            {{ groupedLogDetails[0].userDisplay }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="caption"
                            >Level</v-list-item-title
                          >
                          <v-list-item-subtitle>
                            <v-chip
                              :color="
                                levelColor(
                                  groupedLogDetails[0].status ||
                                    groupedLogDetails[0].level
                                )
                              "
                              dark
                              small
                            >
                              {{
                                groupedLogDetails[0].status ||
                                (groupedLogDetails[0].level
                                  ? groupedLogDetails[0].level.toUpperCase()
                                  : "")
                              }}
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="caption"
                            >Message</v-list-item-title
                          >
                          <v-list-item-subtitle>
                            {{ groupedLogDetails[0].message }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div>
                      <div class="caption mb-1">Full Log Entry</div>
                      <pre class="log-detail-pre"
                        >{{
                          groupedLogDetails
                            .map((log) => log.rawLine)
                            .filter(Boolean)
                            .join("\n")
                        }}
                      </pre>
                    </div>
                  </v-col>
                </v-row>
              </div>
              <div v-else>
                <v-alert type="info">No details found for this event.</v-alert>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import { apiConfig } from "@/config/api.config";
export default {
  name: "LogDashboard",
  data() {
    return {
      logFiles: [],
      selectedFile: null,
      logEntries: [],
      loading: false,
      stats: { error: 0, warn: 0, info: 0, success: 0 },
      filters: {
        keyword: "",
        level: "",
      },
      logFileSearch: "",
      filterDateFrom: null,
      filterDateTo: null,
      filterType: null,
      filterSize: null,
      fromMenu: false,
      toMenu: false,
      deleteDialog: false,
      bulkDeleteDialog: false,
      fileToDelete: null,
      logDetailDialog: false,
      selectedLogDetail: null,
      groupedLogDetails: [],
      logFileTypes: [
        { text: "All", value: null },
        { text: "Main", value: "merimen" },
        { text: "Error", value: "error" },
        { text: "Uncaught", value: "uncaught" },
      ],
      logFileSizes: [
        { text: "All", value: null },
        { text: "Empty", value: "empty" },
        { text: "< 100 KB", value: "small" },
        { text: "100 KB - 1 MB", value: "medium" },
        { text: "> 1 MB", value: "large" },
      ],
      logLevels: [
        { text: "All", value: "" },
        { text: "Error", value: "error" },
        { text: "Warn", value: "warn" },
        { text: "Info", value: "info" },
        { text: "Debug", value: "debug" },
      ],
      headers: [
        { text: "Timestamp", value: "timestamp", width: 180 },
        { text: "Level", value: "level", width: 100 },
        { text: "User", value: "userDisplay", width: 220 },
        { text: "Event", value: "eventTitle", width: 200 },
        // Removed Actions column
      ],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        total: 0,
      },
    };
  },
  computed: {
    filteredLogFiles() {
      let files = this.logFiles;
      // Search by name
      if (this.logFileSearch) {
        const search = this.logFileSearch.toLowerCase();
        files = files.filter((f) => f.name.toLowerCase().includes(search));
      }
      // Filter by type
      if (this.filterType) {
        if (this.filterType === "merimen") {
          files = files.filter(
            (f) =>
              f.name.startsWith("merimen-") &&
              !f.name.includes("error") &&
              !f.name.includes("uncaught")
          );
        } else if (this.filterType === "error") {
          files = files.filter((f) => f.name.includes("error"));
        } else if (this.filterType === "uncaught") {
          files = files.filter((f) => f.name.includes("uncaught"));
        }
      }
      // Filter by size
      if (this.filterSize) {
        files = files.filter((f) => {
          if (this.filterSize === "empty") return f.size === 0;
          if (this.filterSize === "small") return f.size > 0 && f.size < 102400;
          if (this.filterSize === "medium")
            return f.size >= 102400 && f.size < 1048576;
          if (this.filterSize === "large") return f.size >= 1048576;
          return true;
        });
      }
      // Filter by date
      if (this.filterDateFrom) {
        const from = new Date(this.filterDateFrom).setHours(0, 0, 0, 0);
        files = files.filter((f) => new Date(f.modified).getTime() >= from);
      }
      if (this.filterDateTo) {
        const to = new Date(this.filterDateTo).setHours(23, 59, 59, 999);
        files = files.filter((f) => new Date(f.modified).getTime() <= to);
      }
      return files;
    },
    hasEmptyFiles() {
      return this.logFiles.some((f) => f.size === 0);
    },
    emptyFiles() {
      return this.logFiles.filter((f) => f.size === 0);
    },
    emptyFilesCount() {
      return this.emptyFiles.length;
    },
    // Remove groupedLogEntries and paginatedGroupedLogEntries, replace with flat sorted entries
    sortedLogEntries() {
      // Sort all log entries by timestamp descending (latest first)
      return [...this.logEntries].sort((a, b) => {
        const ta = new Date(a.timestamp).getTime();
        const tb = new Date(b.timestamp).getTime();
        return tb - ta;
      });
    },
    paginatedLogEntries() {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.sortedLogEntries.slice(start, end);
    },
    summaryStats() {
      // Calculate stats for only the currently paginated log entries
      const stats = { error: 0, warn: 0, info: 0, success: 0 };
      for (const entry of this.paginatedLogEntries) {
        const lvl = (entry.level || "").toLowerCase();
        if (stats[lvl] !== undefined) stats[lvl]++;
      }
      return stats;
    },
  },
  watch: {
    // Remove groupedLogEntries watcher
    // Remove paginatedLogEntries watcher that resets page
    selectedFile: {
      immediate: true,
      handler() {
        this.pagination.page = 1; // Reset page when file changes
        this.fetchLogEntries();
      },
    },
    "filters.keyword": function () {
      this.pagination.page = 1; // Reset page when filters change
      this.fetchLogEntries();
    },
    "filters.level": function () {
      this.pagination.page = 1; // Reset page when filters change
      this.fetchLogEntries();
    },
  },
  filters: {
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString();
    },
    formatDateTime(val) {
      if (!val) return "";
      return new Date(val).toLocaleString();
    },
    formatSize(val) {
      if (!val) return "0 B";
      if (val < 1024) return `${val} B`;
      if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
      return `${(val / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
  created() {
    this.fetchLogFiles();
  },
  methods: {
    async fetchLogFiles() {
      this.loading = true;
      try {
        const res = await axios.get(`${apiConfig.baseURL}/logs/files`);
        this.logFiles = res.data.files;
        // Always re-select the selected file (if still exists), else select the first
        if (
          this.selectedFile &&
          this.logFiles.some((f) => f.name === this.selectedFile.name)
        ) {
          // re-select to trigger fetchLogEntries
          this.selectedFile = this.logFiles.find(
            (f) => f.name === this.selectedFile.name
          );
        } else if (this.logFiles.length) {
          this.selectedFile = this.logFiles[0];
        } else {
          this.selectedFile = null;
          this.logEntries = [];
        }
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to load log files.",
        });
      } finally {
        this.loading = false;
      }
    },
    async selectLogFile(file) {
      this.selectedFile = file;
      // fetchLogEntries will be triggered by watcher
    },
    async fetchLogEntries() {
      if (!this.selectedFile) {
        this.logEntries = [];
        return;
      }
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.pagination;
        const params = {
          file: this.selectedFile.name,
          page,
          limit: itemsPerPage,
          level: this.filters.level,
          keyword: this.filters.keyword,
        };
        const res = await axios.get(`${apiConfig.baseURL}/logs/entries`, {
          params,
        });
        this.logEntries = res.data.entries;
        this.pagination.total = res.data.total;
        this.stats = res.data.stats || {
          error: 0,
          warn: 0,
          info: 0,
          success: 0,
        };
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to load log entries.",
        });
      } finally {
        this.loading = false;
      }
    },
    async downloadLogFile(file) {
      try {
        const res = await axios.get(
          `${apiConfig.baseURL}/logs/download?file=${encodeURIComponent(
            file.name
          )}`,
          {
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to download log file.",
        });
      }
    },
    confirmDeleteLogFile(file) {
      this.fileToDelete = file;
      this.deleteDialog = true;
    },
    async deleteLogFile() {
      if (!this.fileToDelete) {
        this.deleteDialog = false;
        return;
      }
      try {
        await axios.delete(`${apiConfig.baseURL}/logs/file`, {
          params: { file: this.fileToDelete.name },
        });
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "success",
          text: `Deleted log file: ${this.fileToDelete.name}`,
        });
        this.deleteDialog = false;
        this.fetchLogFiles();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to delete log file.",
        });
        this.deleteDialog = false;
      }
    },
    confirmBulkDelete() {
      this.bulkDeleteDialog = true;
    },
    async bulkDeleteEmptyFiles() {
      this.bulkDeleteDialog = false;
      if (!this.emptyFiles.length) return;
      try {
        await axios.delete(`${apiConfig.baseURL}/logs/files/empty`);
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "success",
          text: `Deleted ${this.emptyFiles.length} empty log file${
            this.emptyFiles.length > 1 ? "s" : ""
          }.`,
        });
        this.fetchLogFiles();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to delete empty log files.",
        });
      }
    },
    showLogDetailGroup(item) {
      // Show only the selected log entry in the dialog
      this.groupedLogDetails = [item];
      this.logDetailDialog = true;
    },
    formatLogDetail(log) {
      if (!log) return "";
      // Always show meta as an object, even if empty or not a string
      let metaObj = {};
      if (log.meta && typeof log.meta === "string") {
        try {
          metaObj = JSON.parse(log.meta);
        } catch {
          metaObj = log.meta;
        }
      } else if (log.meta && typeof log.meta === "object") {
        metaObj = log.meta;
      }
      // If meta is empty, show a message
      if (
        !metaObj ||
        (typeof metaObj === "object" && Object.keys(metaObj).length === 0)
      ) {
        return "(No meta data)";
      }
      return JSON.stringify(metaObj, null, 2);
    },
    formatFullLogDetail(log) {
      if (!log) return "";
      // Show the entire log entry, all fields, including userId, email, username
      return JSON.stringify(log, null, 2);
    },
    formatAllLogFields(log) {
      if (log && log.rawLine) {
        return log.rawLine;
      }
      // fallback to JSON string
      let all = { ...log };
      if (
        all.meta &&
        typeof all.meta === "string" &&
        all.meta.trim().startsWith("{")
      ) {
        try {
          all.meta = JSON.parse(all.meta);
        } catch {
          // leave as is
        }
      }
      if (all._group) delete all._group;
      return JSON.stringify(all, null, 2);
    },
    formatFullLogGroup(group) {
      // Defensive: Vue may wrap arrays with Observer, so use Array.from to ensure a plain array
      if (!group || !group.length) return "";
      const arr = Array.isArray(group) ? group : Array.from(group);
      return arr
        .map((log) =>
          log && typeof log.rawLine === "string" && log.rawLine.trim()
            ? log.rawLine
            : JSON.stringify(log, null, 2)
        )
        .join("\n");
    },
    levelColor(levelOrStatus) {
      const val = (levelOrStatus || "").toLowerCase();
      switch (val) {
        case "error":
        case "failure":
          return "red";
        case "warn":
          return "orange";
        case "info":
        case "success":
          return val === "success" ? "green" : "blue";
        case "debug":
          return "grey";
        default:
          return "grey";
      }
    },
    formatEventTitle(title) {
      // Remove trailing '{' and whitespace, if present
      if (!title) return "";
      return title.replace(/\s*\{$/, "").trim();
    },
    clearAllFilters() {
      this.logFileSearch = "";
      this.filterDateFrom = null;
      this.filterDateTo = null;
      this.filterType = null;
      this.filterSize = null;
      this.filters.keyword = "";
      this.filters.level = "";
    },
  },
};
</script>

<style scoped>
.log-files-list {
  max-height: 350px;
  overflow-y: auto;
  border-bottom: 1px solid #eee;
}
.v-list-item--active {
  background: #e3f2fd !important;
}
/* Horizontal summary bar styling */
.summary-bar {
  background: #fafbfc;
  border-radius: 6px;
  box-shadow: none;
}
.summary-label {
  font-weight: 500;
  margin-left: 4px;
  margin-right: 8px;
}
.summary-value {
  font-size: 1.3em;
  font-weight: bold;
  margin-left: 2px;
}
.error--text {
  color: #d32f2f !important;
}
.warning--text {
  color: #ffa000 !important;
}
.info--text {
  color: #1976d2 !important;
}
.success--text {
  color: #388e3c !important;
}
/* Bold white font for DELETE ALL EMPTY button */
.v-btn.white--text.font-weight-bold {
  font-weight: bold !important;
  color: #fff !important;
}
.log-detail-pre {
  background: #23272e;
  color: #e0e0e0;
  padding: 16px;
  border-radius: 6px;
  font-size: 0.95em;
  max-height: 300px;
  overflow: auto;
}
</style>
